import { DataProvider } from '@refinedev/core';
import { api } from '../services/api';
import { Sandbox } from '../types/sandbox';
import { AxiosError } from 'axios';

interface GetListResponse {
  sandboxes: Sandbox[];
  total: number;
}

export const dataProvider = (apiUrl: string): DataProvider => {
  return {
    getList: async ({ resource, pagination, filters, sorters }) => {
      try {
        // Build query parameters
        const params = new URLSearchParams();

        // Pagination
        if (pagination) {
          params.set('page', String(pagination.current ?? 1));
          params.set('per_page', String(pagination.pageSize ?? 10));
        }

        // Filters
        if (filters) {
          filters.forEach((filter) => {
            if ('field' in filter && 'value' in filter && filter.value) {
              // Map filter field to API parameter
              if (filter.field === 'status') {
                params.set('status', String(filter.value));
              } else if (filter.field === 'cluster') {
                params.set('cluster', String(filter.value));
              } else if (filter.field === 'label') {
                // Label format: key=value
                params.set('label', String(filter.value));
              }
            }
          });
        }

        // Sorting
        if (sorters && sorters.length > 0) {
          const sorter = sorters[0];
          const sortField = sorter.field;
          const sortOrder = sorter.order === 'asc' ? 'asc' : 'desc';
          params.set('sort', `${sortField}:${sortOrder}`);
        }

        // Make API request
        const url = `/v1/${resource}${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await api.get<GetListResponse>(url);

        return {
          data: response.data.sandboxes || [],
          total: response.data.total || 0,
        };
      } catch (error) {
        console.error('getList error:', error);
        const axiosError = error as AxiosError;
        throw new Error(axiosError.message || 'Failed to fetch resources');
      }
    },

    getOne: async ({ resource, id }) => {
      try {
        const url = `/v1/${resource}/${id}`;
        const response = await api.get<Sandbox>(url);

        return {
          data: response.data,
        };
      } catch (error) {
        console.error('getOne error:', error);
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 404) {
          throw new Error(`${resource} ${id} not found`);
        }
        throw new Error(axiosError.message || `Failed to fetch ${resource}`);
      }
    },

    create: async ({ resource, variables }) => {
      try {
        const url = `/v1/${resource}`;
        const response = await api.post<Sandbox>(url, variables);

        return {
          data: response.data,
        };
      } catch (error) {
        console.error('create error:', error);
        const axiosError = error as AxiosError<{ error?: { message?: string } }>;
        const errorMessage = axiosError.response?.data?.error?.message || axiosError.message || 'Failed to create resource';
        throw new Error(errorMessage);
      }
    },

    update: async ({ resource, id, variables }) => {
      try {
        const url = `/v1/${resource}/${id}`;
        const response = await api.patch<Sandbox>(url, variables);

        return {
          data: response.data,
        };
      } catch (error) {
        console.error('update error:', error);
        const axiosError = error as AxiosError<{ error?: { message?: string } }>;
        const errorMessage = axiosError.response?.data?.error?.message || axiosError.message || 'Failed to update resource';
        throw new Error(errorMessage);
      }
    },

    deleteOne: async ({ resource, id }) => {
      try {
        const url = `/v1/${resource}/${id}`;
        await api.delete(url);

        return {
          data: { id } as { id: string },
        };
      } catch (error) {
        console.error('deleteOne error:', error);
        const axiosError = error as AxiosError<{ error?: { message?: string } }>;
        const errorMessage = axiosError.response?.data?.error?.message || axiosError.message || 'Failed to delete resource';
        throw new Error(errorMessage);
      }
    },

    getApiUrl: () => apiUrl,
  } as DataProvider;
};
